import React, { useState, useEffect } from 'react';
import { Layout, Table, Button, Spin, message, Typography, Tooltip, Popconfirm, Tabs } from 'antd';
import axios from 'axios';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import DiffCheckerTabs from './DiffCheckerTabs';
import DiffCheckerLogs from './DiffCheckerLogs';
import DiffCheckerSourceCSV from './DiffCheckerSourceCSV';
import { DiffChecker_URL, DateTimeFormat } from "../../Components/Constant";
import dayjs from "dayjs";
import { convertDateToUserTimezone } from '../../Components/Utility';
import Cookies from 'js-cookie';


const { Title } = Typography;
const { Content } = Layout;

const DiffCheckerHome = () => {
  const [activeComponent, setActiveComponent] = useState('table'); // 'table' or 'tabs'
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedComparejouney, setSelectedCompareJourney] = useState(null);
  const userId = Cookies.get('userId');

  // Fetch compare journeys
  const fetchCompareJourneys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${DiffChecker_URL}/comparejourney/all/${userId}`);
      setTableData(res.data.data || []);
    } catch (err) {
      message.error('Failed to fetch compare journeys');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeComponent === 'table') fetchCompareJourneys();
  }, [activeComponent]);

  const handleNew = () => {
    setActiveComponent('tabs'); // show tabs view
    setSelectedCompareJourney(null); // new journey
  };

  const handleEdit = (record) => {
    setActiveComponent('tabs'); // show tabs view
    setSelectedCompareJourney(record);
  };

  const handleBack = () => {
    setActiveComponent('table'); // go back to table
    setSelectedCompareJourney(null);
  };

  const handleDelete = async (record) => {
    setLoading(true);
    try {
      await axios.delete(`${DiffChecker_URL}/comparejourney/delete/${record._id}`);
      message.success('Compare journey deleted');
      fetchCompareJourneys(); // refresh table
    }
    catch (err) {
      message.error('Failed to delete compare journey');
    }
    setLoading(false);
  }

  const renderTable = () => {
    const columns = [
      {
        title: 'Name', dataIndex: 'name', key: 'name', render: (text, record) => (
          <a onClick={() => handleEdit(record)} style={{ cursor: 'pointer' }}>
            {text}
          </a>
        ),
      },
      {
        title: 'Source', dataIndex: 'Source', key: 'Source', render(text, record) {
          return record.Source?.Name || '';
        }
      },
      {
        title: 'Destination', dataIndex: 'Destination', key: 'Destination', render(text, record) {
          return record.Destination?.Name || '';
        }
      },
      {
        title: 'Object', dataIndex: ['objectConfig', 'object'], key: 'object', render(text, record) {
          return record.objectConfig?.object || '';
        }
      },
      {
        title: 'External Id', dataIndex: ['objectConfig', 'externalId'], key: 'externalId', render(text, record) {
          return record.objectConfig?.externalId || '';
        }
      },
      {
        title: 'Child Relationships', dataIndex: 'childData', key: 'childData', render(text, record) {
          return (record.childData || []).map(child => `${child.childObject} (${child.fields.length} fields)`).join(', ');
        }
      },
      {
        title: 'Created At', dataIndex: 'createdAt', key: 'createdAt',
        render: (text) => (
          text && dayjs(text).isValid()
            ? convertDateToUserTimezone(text, DateTimeFormat)
            : ''
        ),

      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <>
            <Tooltip title="Edit" onClick={() => handleEdit(record)}>
              <EditOutlined style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Are you sure you want to delete this item?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined style={{ color: 'red', marginRight: 5, fontSize: '12px', cursor: 'pointer' }} />
              </Popconfirm>
            </Tooltip>
          </>
        )
      }
    ];

    return (
      <div style={{ padding: 16,margin:0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4}>Diff Insight Journeys</Title>
          <Button type="primary" onClick={handleNew}>
            New
          </Button>
        </div>
        <Spin spinning={loading}>
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="_id"
            bordered
          />
        </Spin>
      </div>
    );
  };

  const renderTabs = () => {
    const items = [
      {
        key: "compare",
        label: "Compare",
        children: <DiffCheckerTabs comparejourney={selectedComparejouney} />,
      },
      // {
      //   key: "compareCSV",
      //   label: "Compare with CSV",
      //   children: <DiffCheckerSourceCSV comparejouney={selectedComparejouney} />,
      // },
      {
        key: "logs",
        label: "Logs",
        children: <DiffCheckerLogs comparejouney={selectedComparejouney} />,
      },
    ];

    return (
      <div style={{ padding: 0, position: "relative" }}>
        {/* 🔙 Back Button at top right */}
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{ position: "absolute", top: 2, right: 0, cursor: 'pointer', zIndex: 1000 }}
        >
          Back
        </Button>

        <Tabs defaultActiveKey="compare" items={items} />
      </div>
    );
  };


  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ margin: 0, padding: 0, background: '#fff' }}>
        {activeComponent === 'table' ? renderTable() : renderTabs()}
      </Content>
    </Layout>
  );
};

export default DiffCheckerHome;
